import * as React from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { MetricCard } from '@/components/shared/MetricCard'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useApplyStocktake,
  useStocktakeReview,
  type StocktakeDetail,
} from '@/hooks/useStocktakes'
import { ApiError } from '@/lib/api/rawFetch'
import { cn } from '@/lib/utils'

const TH =
  'tbl-th border-b border-[var(--line-warm)] text-left'

function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n)
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

// Units removed = stock we thought was there and isn't (shrinkage, miscount,
// theft), the key signal on a review, so it keeps red, softened to the status
// red. Units added (found stock) is green. The colour sits on the value; the
// MetricCard's brand rule is identity only.
const UP = 'text-[var(--status-approved-fg)]'
const DOWN = 'text-[var(--status-rejected-fg)]'

function Stat({
  label,
  value,
  tone,
  testid,
}: {
  label: string
  value: string
  tone?: 'up' | 'down'
  testid?: string
}) {
  return (
    <div data-testid={testid}>
      <MetricCard
        label={label}
        value={
          <span
            className={cn('tabular-nums', tone === 'up' && UP, tone === 'down' && DOWN)}
          >
            {value}
          </span>
        }
      />
    </div>
  )
}

interface StocktakeReviewProps {
  session: StocktakeDetail
  /** Reopen for another pass — the page owns the status mutation. */
  onBackToCounting: () => void
  statusPending: boolean
}

// The Stock Adjustment preview: only the variants whose Adjustment is nonzero,
// read against LIVE Shopify stock (never the frozen Available baseline the count
// sheet shows), which is why the numbers here can differ from the count and why
// Refresh exists.
export function StocktakeReview({
  session,
  onBackToCounting,
  statusPending,
}: StocktakeReviewProps) {
  const id = String(session.session_id)
  const qc = useQueryClient()

  const {
    data: review,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useStocktakeReview(id, session.status === 'review')
  const applyMutation = useApplyStocktake(id)

  const [confirmOpen, setConfirmOpen] = React.useState(false)
  // Whatever the last apply attempt said, which is fresher than the copy the
  // detail query carries until it refetches.
  const [applyError, setApplyError] = React.useState<string | null>(null)
  const [adjustmentId, setAdjustmentId] = React.useState<number | null>(null)

  const lines = review?.lines ?? []
  const variantsAdjusted = review?.variants_adjusted ?? 0
  const unitsAdded = review?.units_added ?? 0
  const unitsRemoved = review?.units_removed ?? 0
  const net = review?.total_planned_delta ?? 0
  const uncounted = review?.uncounted_lines ?? 0
  const locationName = review?.location_name || session.location_name || 'this location'
  const banner = applyError ?? session.apply_error
  // A perfect count. Still has to go through /apply — that is what archives the
  // session — but nothing reaches Shopify, so it is framed as completing rather
  // than adjusting. Leaving it unapplyable stranded perfect counts in Open.
  const isPerfect = variantsAdjusted === 0

  function handleApply() {
    applyMutation.mutate(undefined, {
      onSuccess: (result) => {
        setConfirmOpen(false)
        if (result.ok) {
          setApplyError(null)
          setAdjustmentId(result.adjustment_id ?? null)
          toast.success(
            result.adjusted === 0
              ? 'Stocktake archived — no adjustments were needed'
              : `Adjustment applied to Shopify — ${result.adjusted} ${plural(
                  result.adjusted,
                  'variant',
                  'variants'
                )} adjusted`
          )
          return
        }
        // HTTP 200 with ok=false: Shopify refused the batch. The session stays
        // in 'review' so the same button is the retry.
        setApplyError(result.error ?? 'Shopify rejected the adjustment')
        toast.error('Apply failed — the count is still in review, so you can retry')
      },
      onError: (err) => {
        setConfirmOpen(false)
        if (err instanceof ApiError && err.status === 503) {
          const msg = 'Shopify is not configured / offline mode — cannot apply'
          setApplyError(msg)
          toast.error(msg)
          return
        }
        if (err instanceof ApiError && err.status === 409) {
          toast.error('This count is no longer in review — reloading it')
          qc.invalidateQueries({ queryKey: ['stocktake', id] })
          return
        }
        const msg = err instanceof Error ? err.message : 'Could not apply the adjustment'
        setApplyError(msg)
        toast.error(msg)
      },
    })
  }

  if (isLoading) {
    return (
      <div
        className="border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[16px] text-xs text-[var(--mute)]"
        data-testid="stocktake-review-loading"
      >
        Checking live Shopify stock…
      </div>
    )
  }

  if (error) {
    const offline = error instanceof ApiError && error.status === 503
    return (
      <div
        className="flex items-center gap-3 border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[16px]"
        data-testid="stocktake-review-error"
      >
        <p className="text-xs text-[var(--status-rejected-fg)]">
          {offline
            ? 'Shopify is not configured / offline mode — the adjustment cannot be previewed.'
            : error instanceof Error
              ? error.message
              : 'Could not load the adjustment preview'}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="h-[22px] border-[var(--line-warm)] px-2 text-[11px]"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col bg-[var(--surface-card)]"
      data-testid="stocktake-review"
    >
      {/* Stats + actions */}
      <div className="flex items-center gap-2 border-b border-[var(--line-warm)] px-[18px] py-1">
        <Stat
          label="Variants adjusted"
          value={String(variantsAdjusted)}
          testid="review-variants-adjusted"
        />
        <Stat
          label="Units added"
          value={String(unitsAdded)}
          tone={unitsAdded > 0 ? 'up' : undefined}
          testid="review-units-added"
        />
        <Stat
          label="Units removed"
          value={String(unitsRemoved)}
          tone={unitsRemoved > 0 ? 'down' : undefined}
          testid="review-units-removed"
        />
        <Stat label="Net" value={signed(net)} testid="review-net" />
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-[22px] px-2 text-[11px] text-[var(--ink-2)] hover:text-[var(--text-primary)]"
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="review-refresh-btn"
          >
            <RefreshCw className={cn('mr-1 h-3 w-3', isFetching && 'animate-spin')} />
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-[22px] border-[var(--text-primary)] px-2 text-[11px] font-semibold text-[var(--text-primary)]"
            onClick={onBackToCounting}
            disabled={statusPending}
            data-testid="review-back-to-counting-btn"
          >
            {statusPending ? 'Reopening…' : 'Back to Counting'}
          </Button>
          {/* Any user can apply: whoever walked the rack is who closes the count. */}
          <Button
            size="sm"
            className="h-[22px] px-2 text-[11px] font-semibold"
            onClick={() => setConfirmOpen(true)}
            disabled={applyMutation.isPending}
            data-testid="review-apply-btn"
          >
            {applyMutation.isPending
              ? 'Applying…'
              : isPerfect
                ? 'Complete Stocktake'
                : banner
                  ? 'Retry apply to Shopify…'
                  : 'Apply to Shopify…'}
          </Button>
        </div>
      </div>

      {banner && (
        <p
          className="border-b border-[var(--line-warm)] bg-[var(--status-rejected-bg)] px-[18px] py-1.5 text-xs text-[var(--status-rejected-fg)]"
          data-testid="review-apply-error"
        >
          Last apply failed: {banner}. Nothing was left half-applied — deltas are
          recomputed against live stock on every attempt, so retrying is safe.
        </p>
      )}

      {/* The other half of the count: what is NOT about to move. Without this
          the review reads as "the whole stocktake is these N rows". */}
      {uncounted > 0 && (
        <p
          className="border-b border-[var(--line-warm)] bg-[var(--surface-page)] px-[18px] py-1.5 text-xs text-[var(--ink-2)]"
          data-testid="review-uncounted-note"
        >
          {uncounted} {plural(uncounted, 'item', 'items')} {plural(uncounted, 'was', 'were')}{' '}
          never counted and will NOT be adjusted — check the Missed tab if that&apos;s
          wrong.
        </p>
      )}

      {adjustmentId != null && (
        <p className="border-b border-[var(--line-warm)] px-[18px] py-1.5 text-xs">
          <Link
            to={`/adjustments/${adjustmentId}`}
            className="font-semibold text-[var(--brand)] underline"
            data-testid="review-adjustment-link"
          >
            View Adjustment #{adjustmentId}
          </Link>
        </p>
      )}

      {/* Discrepancies only — the server sends nothing that isn't moving. */}
      {lines.length === 0 ? (
        <div
          className="px-[18px] py-6 text-xs text-[var(--mute)]"
          data-testid="review-no-discrepancies"
        >
          Every counted variant already matches Shopify — there is nothing to adjust.
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <table className="w-full table-fixed border-collapse">
            <colgroup>
              <col />
              <col style={{ width: 150 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 180 }} />
            </colgroup>
            <thead>
              <tr>
                <th className={TH}>Product</th>
                <th className={TH}>Variant</th>
                <th className={TH}>SKU</th>
                <th className={`${TH} text-right whitespace-normal`}>Old Available Quantity</th>
                <th className={`${TH} text-right`}>Adjustment</th>
                <th className={`${TH} text-right whitespace-normal`}>New Available Quantity</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, index) => {
                const d = l.planned_delta ?? 0
                return (
                  <tr
                    key={l.line_id}
                    className={
                      index % 2 === 1
                        ? 'bg-[var(--surface-zebra)]'
                        : 'bg-[var(--surface-card)]'
                    }
                    data-testid={`review-row-${l.line_id}`}
                  >
                    <td className="border-b border-[var(--line-warm)] px-2 py-1.5 text-xs font-medium text-[var(--text-primary)]">
                      {l.product_title || '—'}
                    </td>
                    <td className="border-b border-[var(--line-warm)] px-2 py-1.5 text-xs text-[var(--ink-2)]">
                      {l.variant_title || '—'}
                    </td>
                    <td className="border-b border-[var(--line-warm)] px-2 py-1.5 font-mono text-xs text-[var(--ink-2)]">
                      {l.sku || '—'}
                    </td>
                    <td className="border-b border-[var(--line-warm)] px-2 py-1.5 text-right text-xs tabular-nums text-[var(--ink-2)]">
                      {l.live_on_hand ?? '—'}
                    </td>
                    <td className="border-b border-[var(--line-warm)] px-2 py-1.5 text-right text-xs tabular-nums">
                      <span
                        className={cn(
                          'font-semibold',
                          d > 0 && UP,
                          d < 0 && DOWN
                        )}
                        data-testid={`review-delta-${l.line_id}`}
                      >
                        {signed(d)}
                      </span>
                    </td>
                    <td className="border-b border-[var(--line-warm)] px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-[var(--text-primary)]">
                      {l.counted_qty ?? '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Apply confirm — one of the two places in the app that write stock to
          Shopify (the other is applying an Adjustment). The confirm button says
          exactly what it does, so the weight is in the wording, not a colour. */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)]"
          data-testid="review-apply-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {isPerfect ? 'Complete this stocktake?' : 'Apply this adjustment?'}
            </DialogTitle>
            <DialogDescription asChild>
              {isPerfect ? (
                <div className="flex flex-col gap-2 text-xs">
                  <p>
                    Every counted variant already matches Shopify, so no adjustment
                    will be made and Shopify will not be touched.
                  </p>
                  <p>The stocktake will be archived.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-2 text-xs">
                  <ul className="flex flex-col gap-0.5 tabular-nums">
                    <li>
                      Variants being adjusted: <strong>{variantsAdjusted}</strong>
                    </li>
                    <li>
                      Units being added: <strong>{unitsAdded}</strong>
                    </li>
                    <li>
                      Units being removed: <strong>{unitsRemoved}</strong>
                    </li>
                  </ul>
                  <p>
                    This will update Shopify inventory at {locationName}.{' '}
                    <span
                      className="font-semibold text-[var(--status-awaiting-fg)]"
                      data-testid="review-apply-caution"
                    >
                      This cannot be undone.
                    </span>
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setConfirmOpen(false)
                onBackToCounting()
              }}
              disabled={applyMutation.isPending || statusPending}
              data-testid="review-dialog-back-btn"
            >
              Back to Counting
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              disabled={applyMutation.isPending}
              data-testid="review-apply-confirm-btn"
            >
              {applyMutation.isPending
                ? 'Applying…'
                : isPerfect
                  ? 'Complete & Archive'
                  : `Apply ${variantsAdjusted} ${plural(
                      variantsAdjusted,
                      'change',
                      'changes'
                    )} to Shopify`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
